import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import PrintQuotation from '@/components/print/PrintQuotation';
import { useToast } from '@/contexts/ToastContext';
import { type MockQuotation } from '@/mocks/billing';
import { quotationService } from '@/services/quotationService';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  SENT: 'bg-blue-50 text-blue-700 border-blue-200',
  ACCEPTED: 'bg-teal-50 text-teal-700 border-teal-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  EXPIRED: 'bg-orange-50 text-orange-700 border-orange-200',
  CONVERTED_TO_SO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CONVERTED_TO_PI: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent to Customer',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  CONVERTED_TO_SO: '→ SO Created',
  CONVERTED_TO_PI: '→ PI Created',
};

const today = new Date().toISOString().split('T')[0];
const sevenDaysFromNow = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

export default function QuotationsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedWarehouseId: topWarehouseId } = useWarehouseStore();
  const [quotations, setQuotations] = useState<MockQuotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(topWarehouseId || 'ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [printQuotationData, setPrintQuotationData] = useState<MockQuotation | null>(null);

  // Sync with global topbar warehouse selector
  useEffect(() => {
    if (topWarehouseId) {
      setSelectedWarehouseId(topWarehouseId);
    }
  }, [topWarehouseId]);

  // Load warehouses
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getAllWarehouses();
        if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
          setWarehouses(res.data.map((w: WarehouseResponse) => ({ id: w.id, name: w.name })));
        }
      } catch (e) {
        console.warn('Failed to load warehouses:', e);
      }
    }
    loadWarehouses();
  }, []);

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await quotationService.listQuotations({
        search,
        status: statusFilter,
        warehouseId: selectedWarehouseId,
        dateFrom,
        dateTo,
      });
      setQuotations(data || []);
    } catch (err) {
      console.warn('Backend API error fetching quotations:', err);
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, selectedWarehouseId, dateFrom, dateTo]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  const toggleDropdown = useCallback((id: string) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
  }, []);

  // Summary cards
  const activeCount = quotations.filter((q) => q.status === 'DRAFT' || q.status === 'SENT').length;
  const expiringSoonCount = quotations.filter(
    (q) =>
      q.validUntil <= sevenDaysFromNow &&
      q.validUntil >= today &&
      q.status !== 'EXPIRED' &&
      q.status !== 'CONVERTED_TO_SO' &&
      q.status !== 'CONVERTED_TO_PI' &&
      q.status !== 'REJECTED'
  ).length;
  const convertedCount = quotations.filter(
    (q) =>
      q.status === 'CONVERTED_TO_SO' ||
      q.status === 'CONVERTED_TO_PI' ||
      (q as any).convertedToSOId ||
      (q as any).convertedToPIId
  ).length;
  const pipelineValue = quotations
    .filter((q) => q.status === 'DRAFT' || q.status === 'SENT')
    .reduce((s, q) => s + q.totalAmount, 0);

  // Client-side filtering fallback for quick UI responsiveness
  const filtered = useMemo(() => {
    return quotations.filter((qt) => {
      if (statusFilter !== 'All' && qt.status !== statusFilter) return false;
      if (selectedWarehouseId !== 'ALL' && qt.warehouseId && qt.warehouseId !== selectedWarehouseId) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!qt.quotationNumber.toLowerCase().includes(s) && !qt.customerName.toLowerCase().includes(s)) return false;
      }
      if (dateFrom && qt.date < dateFrom) return false;
      if (dateTo && qt.date > dateTo) return false;
      return true;
    });
  }, [quotations, search, statusFilter, selectedWarehouseId, dateFrom, dateTo]);

  const isExpiredByDate = (qt: MockQuotation) => {
    return (qt.status === 'DRAFT' || qt.status === 'SENT') && qt.validUntil < today;
  };

  const handleSend = async (qt: MockQuotation, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await quotationService.updateStatus(qt.id, 'SENT');
      toast.success(`Quotation ${qt.quotationNumber} marked as sent`);
      fetchQuotations();
    } catch {
      qt.status = 'SENT';
      setQuotations([...quotations]);
      toast.success(`Quotation ${qt.quotationNumber} marked as sent`);
    }
  };

  const handleCancel = async (qt: MockQuotation, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await quotationService.updateStatus(qt.id, 'REJECTED');
      toast.info(`Quotation ${qt.quotationNumber} rejected`);
      fetchQuotations();
    } catch {
      qt.status = 'REJECTED';
      setQuotations([...quotations]);
      toast.info(`Quotation ${qt.quotationNumber} rejected`);
    }
  };

  const handleConvertToSO = (qt: MockQuotation, e: React.MouseEvent) => {
    e.stopPropagation();
    const targetSoId = (qt as any).convertedToSOId || (qt as any).resolved_so_id || (qt as any).salesOrderId;
    if (targetSoId || qt.status === 'CONVERTED_TO_SO') {
      if (targetSoId) {
        navigate(`/sales/orders/${targetSoId}`);
      } else {
        navigate('/sales/orders');
      }
      return;
    }

    navigate('/sales/orders/new', {
      state: {
        fromQuotation: {
          fromQuotation: true,
          quotationId: qt.id,
          quotationNumber: qt.quotationNumber,
          customerId: qt.customerId,
          customerName: qt.customerName,
          customerGSTIN: qt.customerGSTIN,
          warehouseId: qt.warehouseId,
          paymentTerms: qt.paymentTerms,
          deliveryTerms: qt.deliveryTerms,
          notes: qt.notes,
          items: (qt.items || []).map((i) => ({
            itemId: i.itemId,
            itemCode: (i as any).itemCode || '',
            itemName: i.itemName,
            hsnCode: i.hsnCode || '',
            description: i.description || '',
            qty: i.qty,
            unit: i.unit || 'Pcs',
            unitId: (i as any).unitId || '',
            rate: i.rate,
            discount: (i as any).discountPercent ?? (i as any).discount ?? 0,
            taxRate: i.taxRate ?? 18,
          })),
        },
      },
    });
  };

  const handleCreatePI = (qt: MockQuotation, e: React.MouseEvent) => {
    e.stopPropagation();
    const targetPiId = (qt as any).convertedToPIId || (qt as any).resolved_pi_id || (qt as any).proformaInvoiceId;
    if (targetPiId || qt.status === 'CONVERTED_TO_PI') {
      if (targetPiId) {
        navigate(`/sales/proforma/${targetPiId}`);
      } else {
        navigate('/sales/proforma');
      }
      return;
    }

    navigate('/sales/proforma/new', {
      state: {
        fromQuotation: {
          quotationId: qt.id,
          quotationNumber: qt.quotationNumber,
          customerId: qt.customerId,
          customerName: qt.customerName,
          customerGSTIN: qt.customerGSTIN,
          warehouseId: qt.warehouseId,
          paymentTerms: qt.paymentTerms,
          deliveryTerms: qt.deliveryTerms,
          notes: qt.notes,
          items: (qt.items || []).map((i) => ({
            itemId: i.itemId,
            itemCode: (i as any).itemCode || '',
            itemName: i.itemName,
            hsnCode: i.hsnCode || '',
            description: i.description || '',
            qty: i.qty,
            unit: i.unit || 'Pcs',
            unitId: (i as any).unitId || '',
            rate: i.rate,
            discount: (i as any).discountPercent ?? (i as any).discount ?? 0,
            taxRate: i.taxRate ?? 18,
          })),
        },
      },
    });
  };

  const handlePrint = (qt: MockQuotation, e: React.MouseEvent) => {
    e.stopPropagation();
    setPrintQuotationData(qt);
    setOpenDropdown(null);
  };

  const renderStatusBadge = (qt: MockQuotation) => {
    const expired = isExpiredByDate(qt);
    const styleKey = expired ? 'EXPIRED' : qt.status;
    const label = expired ? 'Expired' : STATUS_LABELS[qt.status] || qt.status;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[styleKey]}`}>
        {label}
      </span>
    );
  };

  const renderActionButtons = (qt: MockQuotation) => {
    const expired = isExpiredByDate(qt);
    const isDropdownOpen = openDropdown === qt.id;

    const actionBtnBase =
      'h-8 px-3 text-xs font-medium rounded-lg cursor-pointer transition-colors inline-flex items-center gap-1 whitespace-nowrap';
    const iconBtnBase =
      'w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer transition-colors';

    const targetSoId = (qt as any).convertedToSOId || (qt as any).resolved_so_id || (qt as any).salesOrderId;
    const targetPiId = (qt as any).convertedToPIId || (qt as any).resolved_pi_id || (qt as any).proformaInvoiceId;
    const hasSO = Boolean(qt.status === 'CONVERTED_TO_SO' || targetSoId);
    const hasPI = Boolean(qt.status === 'CONVERTED_TO_PI' || targetPiId);
    const soNumber = (qt as any).convertedToSONumber || (qt as any).resolved_so_number || (qt as any).salesOrderNumber || (qt as any).salesOrderNo || '';
    const piNumber = (qt as any).convertedToPINumber || (qt as any).resolved_pi_number || (qt as any).proformaInvoiceNumber || (qt as any).proformaInvoiceNo || (qt as any).piNumber || '';
    const isNotDraftOrRejected = qt.status !== 'DRAFT' && qt.status !== 'REJECTED' && !expired;

    return (
      <div className="flex items-center gap-1.5 justify-end">
        {/* Proforma Action Button */}
        {hasPI ? (
          <button onClick={(e) => handleCreatePI(qt, e)} className={`${actionBtnBase} bg-emerald-600 text-white hover:bg-emerald-700`}>
            <i className="ri-bill-line text-xs" /> View PI
          </button>
        ) : (
          isNotDraftOrRejected && (
            <button onClick={(e) => handleCreatePI(qt, e)} className={`${actionBtnBase} bg-teal-600 text-white hover:bg-teal-700`}>
              <i className="ri-bill-line text-xs" /> Create PI
            </button>
          )
        )}

        {/* Sales Order Action Button */}
        {hasSO ? (
          <button onClick={(e) => handleConvertToSO(qt, e)} className={`${actionBtnBase} bg-emerald-600 text-white hover:bg-emerald-700`}>
            <i className="ri-file-check-line text-xs" /> View SO
          </button>
        ) : (
          isNotDraftOrRejected && (
            <button onClick={(e) => handleConvertToSO(qt, e)} className={`${actionBtnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
              <i className="ri-file-copy-line text-xs" /> Convert to SO
            </button>
          )
        )}

        {/* Draft Send Button */}
        {qt.status === 'DRAFT' && (
          <button onClick={(e) => handleSend(qt, e)} className={`${actionBtnBase} bg-blue-600 text-white hover:bg-blue-700`}>
            <i className="ri-send-plane-line text-xs" /> Send
          </button>
        )}

        {/* Detail View Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenDropdown(null);
            navigate(`/sales/quotations/${qt.id}`);
          }}
          className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50`}
        >
          <i className="ri-eye-line text-xs" /> View
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenDropdown(null);
            navigate(`/sales/quotations/new?edit=${qt.id}`);
          }}
          className={`${actionBtnBase} border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-indigo-600`}
          title="Edit Quotation"
        >
          <i className="ri-edit-line text-xs" /> Edit
        </button>

        {/* More Actions Dropdown */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown(qt.id);
            }}
            className={iconBtnBase}
          >
            <i className="ri-more-2-fill text-base" />
          </button>
          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(null);
                }}
              />
              <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-slate-200 rounded-lg shadow-xl z-40 py-1 text-left">
                {qt.status === 'DRAFT' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(null);
                      navigate(`/sales/quotations/new?edit=${qt.id}`);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <i className="ri-edit-line text-sm text-slate-400" /> Edit
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(null);
                    handlePrint(qt, e);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <i className="ri-printer-line text-sm text-slate-400" /> Print
                </button>
                {(qt.status === 'DRAFT' || qt.status === 'SENT') && (
                  <>
                    <div className="border-t border-slate-100 my-1" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(null);
                        handleCancel(qt, e);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 cursor-pointer transition-colors"
                    >
                      <i className="ri-close-circle-line text-sm" /> Cancel
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Quotations</h1>
              <p className="text-sm text-slate-500">Customer price quotations and proposals</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/sales/quotations/new')}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-add-line" /> New Quotation
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Active Quotations</div>
              <div className="text-2xl font-bold text-blue-600">{activeCount}</div>
              <div className="text-xs text-slate-400 mt-0.5">Awaiting response</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Expiring Soon</div>
              <div className="text-2xl font-bold text-amber-600">{expiringSoonCount}</div>
              <div className="text-xs text-slate-400 mt-0.5">Need follow-up</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Converted to SO</div>
              <div className="text-2xl font-bold text-emerald-600">{convertedCount}</div>
              <div className="text-xs text-slate-400 mt-0.5">Total converted</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Pipeline Value</div>
              <div className="text-2xl font-bold text-slate-800">{formatINR(pipelineValue)}</div>
              <div className="text-xs text-slate-400 mt-0.5">Active quotations</div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by QT number or customer..."
                  className="w-full h-9 px-3 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700"
                />
              </div>

              {/* Warehouse Filter */}
              <select
                value={selectedWarehouseId}
                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700 cursor-pointer"
              >
                <option value="ALL">All Warehouses</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 text-slate-700 cursor-pointer"
              >
                <option value="All">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="SENT">Sent</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="EXPIRED">Expired</option>
                <option value="CONVERTED_TO_SO">Converted to SO</option>
              </select>

              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 px-2.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700"
                  title="From date"
                />
                <span className="text-xs text-slate-400">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 px-2.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700"
                  title="To date"
                />
              </div>

              {(search || statusFilter !== 'All' || selectedWarehouseId !== 'ALL' || dateFrom || dateTo) && (
                <button
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('All');
                    setSelectedWarehouseId('ALL');
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="h-9 px-3 text-xs text-indigo-600 hover:bg-indigo-50 rounded-lg font-medium cursor-pointer transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Quotations Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            {loading ? (
              <div className="flex items-center justify-center h-48 text-slate-400">
                <i className="ri-loader-4-line text-2xl animate-spin mr-2" />
                <span className="text-sm">Loading quotations...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-file-text-line text-4xl text-slate-300 block mb-2" />
                <p className="text-slate-500 font-medium text-sm">No quotations found</p>
                <p className="text-slate-400 text-xs mt-1">Try adjusting your filters or create a new quotation.</p>
              </div>
            ) : (
              <div className="overflow-x-auto min-h-[320px] pb-12">
                <table className="w-full min-w-[1000px] text-sm text-left">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Quotation #</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Customer</th>
                      <th className="px-4 py-3">Valid Until</th>
                      <th className="px-4 py-3 text-right">Items</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((qt) => {
                      const expired = isExpiredByDate(qt);
                      return (
                        <tr
                          key={qt.id}
                          onClick={() => navigate(`/sales/quotations/${qt.id}`)}
                          className="hover:bg-slate-50/70 cursor-pointer transition-colors group"
                        >
                          <td className="px-4 py-3.5 font-semibold text-indigo-600 whitespace-nowrap">
                            <span className="group-hover:underline cursor-pointer" onClick={() => navigate(`/sales/quotations/${qt.id}`)}>{qt.quotationNumber}</span>
                          </td>
                          <td className="px-4 py-3.5 text-slate-600 text-xs whitespace-nowrap">
                            {new Date(qt.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3.5 font-medium text-slate-800">
                            <div>{qt.customerName}</div>
                            {qt.customerGSTIN && (
                              <div className="text-[11px] text-slate-400 font-normal">GSTIN: {qt.customerGSTIN}</div>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-xs whitespace-nowrap">
                            <span className={expired ? 'text-red-600 font-medium' : 'text-slate-600'}>
                              {new Date(qt.validUntil).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right text-slate-600 text-xs font-mono">
                            {qt.items?.length || 0}
                          </td>
                          <td className="px-4 py-3.5 text-right font-semibold text-slate-800 whitespace-nowrap">
                            {formatINR(qt.totalAmount)}
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">{renderStatusBadge(qt)}</td>
                          <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>{renderActionButtons(qt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Print Modal */}
        {printQuotationData && (
          <PrintQuotation quotation={printQuotationData} onClose={() => setPrintQuotationData(null)} />
        )}
      </div>
    </AppLayout>
  );
}